import { AlertTriangle, CheckCircle } from 'lucide-react';
import type {
  FreeResult,
  WFDimensionDeepDive,
  WFMisfitSignal,
  WorkFitReportContent,
} from '../../lib/apiClient';
import { WF_DIMENSION_LABELS } from '../../lib/assessment/content';

/**
 * The full paid Work Fit report, copied from production's
 * `work-fit-report.tsx`.
 *
 * **Production's headings, order and inner labels.** V2 carried the same
 * content under its own sentence-case titles — "Your profile in depth" for
 * "Profile Deep Dive", "Interview questions" for "Interview Question Bank" —
 * and put Environment Needs before Misfit Risk Signals where production puts
 * them the other way round.
 *
 * The one section V2 had nowhere is **Dimension Score Profile**. Production
 * reads `report.dimensionScores`; V2's payload has no such field, but the free
 * result already carries the same numbers as `categoryScores`, so the section
 * is built from those rather than widening the API for a number it already has.
 */
const DEEP_DIVE_LABELS: Array<{ key: keyof WorkFitReportContent['profileDeepDive']; label: string }> =
  [
    { key: 'whatThisProfileNeeds', label: 'What This Profile Needs' },
    { key: 'whatDrainsThisProfile', label: 'What Drains This Profile' },
    { key: 'howThisProfileEvaluatesRoles', label: 'How This Profile Evaluates Roles' },
    { key: 'whatThisProfileMayOverlook', label: 'What This Profile May Overlook' },
  ];

export function WorkFitReport({
  result,
  content,
  environmentNeeds,
  misfitSignals,
}: {
  result: FreeResult;
  content: WorkFitReportContent;
  environmentNeeds: WFDimensionDeepDive[];
  misfitSignals: WFMisfitSignal[];
}) {
  const {
    profileDeepDive,
    managerFitGuide,
    cultureRiskGuide,
    currentRoleReflection,
    successConditions,
    finalDecisionGuide,
  } = content;

  const topLabels = result.topCategories.map((key) => WF_DIMENSION_LABELS[key] ?? key);
  const dimensionScores = Object.entries(result.categoryScores)
    .map(([key, score]) => ({ key, label: WF_DIMENSION_LABELS[key] ?? key, score }))
    .sort((a, b) => b.score - a.score);

  return (
    <div className="flex flex-col gap-10" data-testid="full-report-work_fit">
      <section
        className="flex flex-col gap-4 rounded-xl border border-primary/20 bg-primary/5 p-8"
        data-testid="work-fit-report-header"
      >
        <p className="text-xs font-bold uppercase tracking-widest text-primary">
          Full Work Fit Report
        </p>
        <h1 className="text-3xl font-bold">{result.primaryType}</h1>
        {topLabels.length > 0 ? (
          <div className="flex flex-wrap gap-2">
            {topLabels.map((label) => (
              <span
                key={label}
                className="rounded-md border border-primary/30 px-2 py-0.5 text-xs text-primary"
              >
                {label}
              </span>
            ))}
          </div>
        ) : null}
      </section>

      <Section title="Profile Deep Dive">
        <div className="grid gap-4 sm:grid-cols-2">
          {DEEP_DIVE_LABELS.map(({ key, label }) => (
            <div key={key} className="flex flex-col gap-2 rounded-xl border border-border bg-card p-5">
              <Eyebrow>{label}</Eyebrow>
              <p className="text-sm leading-relaxed text-muted-foreground">{profileDeepDive[key]}</p>
            </div>
          ))}
        </div>
      </Section>

      {dimensionScores.length > 0 ? (
        <Section title="Dimension Score Profile">
          <div className="flex flex-col gap-3" data-testid="work-fit-dimension-scores">
            {dimensionScores.map((dim) => (
              <div key={dim.key} className="flex items-center gap-3" data-dimension={dim.key}>
                <span className="w-48 shrink-0 text-sm">{dim.label}</span>
                <div className="h-2 flex-1 overflow-hidden rounded-full bg-secondary">
                  <div
                    className="h-full rounded-full bg-primary"
                    style={{ width: `${Math.round((dim.score / 5) * 100)}%` }}
                  />
                </div>
                <span className="w-10 shrink-0 text-right text-sm tabular-nums text-muted-foreground">
                  {dim.score.toFixed(1)}
                </span>
              </div>
            ))}
          </div>
        </Section>
      ) : null}

      {misfitSignals.length > 0 ? (
        <Section title="Misfit Risk Signals">
          <div className="flex flex-col gap-4">
            {misfitSignals.map((dim) => (
              <div
                key={dim.dimension}
                className="flex flex-col gap-3 rounded-xl border border-border bg-card p-5"
              >
                <p className="font-semibold">{dim.label}</p>
                <div>
                  <Eyebrow tone="text-destructive/70">Poor Fit Looks Like</Eyebrow>
                  <p className="text-sm leading-relaxed text-muted-foreground">
                    {dim.poorFitLooksLike}
                  </p>
                </div>
                <div>
                  <Eyebrow tone="text-amber-500/80">Red Flags to Watch For</Eyebrow>
                  <Bullets items={dim.redFlagsToWatchFor} />
                </div>
              </div>
            ))}
          </div>
        </Section>
      ) : null}

      <Section title="Your Environment Needs">
        <div className="flex flex-col gap-4">
          {environmentNeeds.map((dim) => (
            <div
              key={dim.dimension}
              className="flex flex-col gap-3 rounded-xl border border-border bg-card p-5"
            >
              <p className="font-semibold">{dim.label}</p>
              <p className="text-sm leading-relaxed text-muted-foreground">{dim.whatThisMeans}</p>

              <div className="grid gap-4 sm:grid-cols-2">
                <div>
                  <p className="mb-1 flex items-center gap-1.5 text-xs font-bold uppercase tracking-widest text-green-500">
                    <CheckCircle className="h-3.5 w-3.5" aria-hidden="true" />
                    Good Fit Looks Like
                  </p>
                  <p className="text-sm leading-relaxed text-muted-foreground">
                    {dim.goodFitLooksLike}
                  </p>
                </div>
                <div>
                  <p className="mb-1 flex items-center gap-1.5 text-xs font-bold uppercase tracking-widest text-destructive/70">
                    <AlertTriangle className="h-3.5 w-3.5" aria-hidden="true" />
                    Poor Fit Looks Like
                  </p>
                  <p className="text-sm leading-relaxed text-muted-foreground">
                    {dim.poorFitLooksLike}
                  </p>
                </div>
              </div>

              <div>
                <Eyebrow>Questions to Ask</Eyebrow>
                <Bullets items={dim.questionsToAsk} />
              </div>
              <div>
                <Eyebrow tone="text-amber-500/80">Red Flags to Watch For</Eyebrow>
                <Bullets items={dim.redFlagsToWatchFor} />
              </div>
            </div>
          ))}
        </div>
      </Section>

      <Section title="Manager Fit Guide">
        <div className="grid gap-4 sm:grid-cols-2">
          <Panel label="Supportive Manager Style" tone="text-green-500">
            <p className="text-sm leading-relaxed text-muted-foreground">
              {managerFitGuide.supportiveStyle}
            </p>
          </Panel>
          <Panel label="Manager Style That Creates Friction" tone="text-destructive/70">
            <p className="text-sm leading-relaxed text-muted-foreground">
              {managerFitGuide.frictionStyle}
            </p>
          </Panel>
          <Panel label="Questions to Ask the Hiring Manager">
            <Bullets items={managerFitGuide.questionsToAsk} />
          </Panel>
          <Panel label="Warning Signs" tone="text-amber-500/80">
            <Bullets items={managerFitGuide.warningSigns} />
          </Panel>
        </div>
      </Section>

      <Section title="Culture Risk Guide">
        <div className="grid gap-4 sm:grid-cols-2">
          <Panel label="Culture Risks for Your Profile" tone="text-destructive/70">
            <Bullets items={cultureRiskGuide.cultureRisks} />
          </Panel>
          <Panel label="What to Listen For in Interviews">
            <Bullets items={cultureRiskGuide.whatToListenFor} />
          </Panel>
          <Panel label="Language That Can Hide Poor Fit" tone="text-destructive/70">
            <Bullets items={cultureRiskGuide.languageThatHidesPoorFit} />
          </Panel>
          <Panel label="How to Tell Values Are Real" tone="text-green-500">
            <p className="text-sm leading-relaxed text-muted-foreground">
              {cultureRiskGuide.howToTellValuesAreReal}
            </p>
          </Panel>
        </div>
      </Section>

      <Section title="Interview Question Bank">
        <div className="flex flex-col gap-3">
          {content.interviewQuestions.map((q) => (
            <div key={q.question} className="rounded-xl border border-border bg-card p-5">
              <Eyebrow>{q.category}</Eyebrow>
              <p className="text-sm leading-relaxed">{q.question}</p>
            </div>
          ))}
        </div>
      </Section>

      <Section title="Offer Evaluation Checklist">
        <div className="flex flex-col gap-3">
          {content.offerChecklist.map((c) => (
            <div key={c.item} className="rounded-xl border border-border bg-card p-5">
              <Eyebrow>{c.category}</Eyebrow>
              <p className="text-sm leading-relaxed">{c.item}</p>
            </div>
          ))}
        </div>
      </Section>

      <Section title="Current Role Reflection">
        <div className="grid gap-4 sm:grid-cols-2">
          <Panel label="Friction May Be Environmental">
            <Bullets items={currentRoleReflection.frictionMayBeEnvironmental} />
          </Panel>
          <Panel label="What to Clarify With Your Manager">
            <Bullets items={currentRoleReflection.whatToClarifyWithManager} />
          </Panel>
          <Panel label="What to Ask for First">
            <Bullets items={currentRoleReflection.whatToAskForFirst} />
          </Panel>
          <Panel label="Signs the Environment Won't Change" tone="text-destructive/70">
            <Bullets items={currentRoleReflection.signsEnvironmentWontChange} />
          </Panel>
        </div>
      </Section>

      <Section title="30 / 60 / 90 Day Success Conditions">
        <div className="grid gap-4 sm:grid-cols-3">
          <Panel label="By Day 30" tone="text-primary">
            <Bullets items={successConditions.thirtyDay} />
          </Panel>
          <Panel label="By Day 60" tone="text-primary">
            <Bullets items={successConditions.sixtyDay} />
          </Panel>
          <Panel label="By Day 90" tone="text-primary">
            <Bullets items={successConditions.ninetyDay} />
          </Panel>
        </div>
      </Section>

      <Section title="Final Decision Guide">
        <div className="grid gap-4 sm:grid-cols-3">
          <Panel label="Green Lights" tone="text-green-500">
            <Bullets items={finalDecisionGuide.greenLights} />
          </Panel>
          <Panel label="Yellow Flags" tone="text-amber-500/80">
            <Bullets items={finalDecisionGuide.yellowFlags} />
          </Panel>
          <Panel label="Red Flags" tone="text-destructive/70">
            <Bullets items={finalDecisionGuide.redFlags} />
          </Panel>
        </div>
        <Panel label="Decision Prompts">
          <Bullets items={finalDecisionGuide.decisionPrompts} />
        </Panel>
      </Section>
    </div>
  );
}

function Section({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <section className="flex flex-col gap-4" data-testid="work-fit-section" data-title={title}>
      <h2 className="text-xl font-semibold">{title}</h2>
      {children}
    </section>
  );
}

function Eyebrow({
  children,
  tone = 'text-muted-foreground',
}: {
  children: React.ReactNode;
  tone?: string;
}) {
  return (
    <p className={`mb-1 text-xs font-bold uppercase tracking-widest ${tone}`}>{children}</p>
  );
}

function Panel({
  label,
  tone,
  children,
}: {
  label: string;
  tone?: string;
  children: React.ReactNode;
}) {
  return (
    <div className="flex flex-col gap-1 rounded-xl border border-border bg-card p-5">
      <Eyebrow {...(tone ? { tone } : {})}>{label}</Eyebrow>
      {children}
    </div>
  );
}

function Bullets({ items }: { items: string[] }) {
  return (
    <ul className="flex flex-col gap-1.5">
      {items.map((item) => (
        <li key={item} className="text-sm leading-relaxed text-muted-foreground">
          {item}
        </li>
      ))}
    </ul>
  );
}
